import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { PublicLayout, Container } from '../../components/layout';
import { ListingCard } from '../../components/listings/ListingCard';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import {
  CITIES,
  TYPES,
  findCity,
  findType,
  listingMatchesCity,
  listingMatchesType,
  type CityContent,
  type TypeContent,
} from './landingContent';

const SITE_URL = 'https://darak.online';

interface RawListing {
  id: string;
  title: string;
  photos: Array<{ url: string; thumbnail?: string }>;
  propertyType: string;
  location: { city: string; area?: string; country?: string };
  pricing: { nightlyRate: number; currency: string };
  rating?: { average?: number; count?: number };
  likeCount?: number;
  capacity?: { bedrooms?: number; beds?: number; guests?: number };
  isFeatured?: boolean;
  hostId?: string;
  host?: { displayName?: string; photoURL?: string };
  status?: string;
}

interface LandingPageProps {
  mode: 'city' | 'type' | 'cityType';
}

export const LandingPage: React.FC<LandingPageProps> = ({ mode }) => {
  const params = useParams<{ city?: string; type?: string }>();
  const navigate = useNavigate();
  const [listings, setListings] = useState<RawListing[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const city = mode === 'city' || mode === 'cityType' ? findCity(params.city || '') : undefined;
  const type = mode === 'type' || mode === 'cityType' ? findType(params.type || '') : undefined;

  // Redirect to 404-like state if slug doesn't resolve
  useEffect(() => {
    if (mode === 'city' && !city) navigate('/search', { replace: true });
    if (mode === 'type' && !type) navigate('/search', { replace: true });
    if (mode === 'cityType' && (!city || !type)) navigate('/search', { replace: true });
  }, [mode, city, type, navigate]);

  // Fetch and filter listings
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      try {
        const q = query(collection(db, 'listings'), where('status', '==', 'published'));
        const snap = await getDocs(q);
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as RawListing[];
        if (cancelled) return;
        const filtered = all.filter((l) => {
          if (city && !listingMatchesCity(l, city)) return false;
          if (type && !listingMatchesType(l, type)) return false;
          return true;
        });
        setListings(filtered);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [city, type]);

  const meta = useMemo(() => {
    if (city && type) {
      return {
        title: `${type.plural} in ${city.name}, Lebanon | DARAK`,
        description: `${type.plural} for rent in ${city.name}. ${city.intro}`,
        canonical: `${SITE_URL}/lebanon/${city.slug}/${type.slug}`,
        h1: `${type.plural} in ${city.name}`,
        intro: type.intro,
        body: [...city.body, ...type.body],
      };
    }
    if (city) {
      return {
        title: `Vacation rentals in ${city.name}, Lebanon | DARAK`,
        description: `${city.intro} Browse ${city.name} rentals on DARAK.`,
        canonical: `${SITE_URL}/lebanon/${city.slug}`,
        h1: `Stay in ${city.name}`,
        intro: city.intro,
        body: city.body,
      };
    }
    if (type) {
      return {
        title: `${type.plural} in Lebanon | DARAK`,
        description: `${type.intro} Browse all DARAK ${type.plural.toLowerCase()}.`,
        canonical: `${SITE_URL}/property/${type.slug}`,
        h1: `${type.plural} in Lebanon`,
        intro: type.intro,
        body: type.body,
      };
    }
    return null;
  }, [city, type]);

  useDocumentMeta({
    title: meta?.title || 'DARAK',
    description: meta?.description,
    canonical: meta?.canonical,
    ogImage: listings[0]?.photos?.[0]?.url,
  });

  if (!meta) return null;

  // Map for ListingCard
  const cards = listings.map((l) => ({
    id: l.id,
    title: l.title,
    photos: l.photos || [],
    propertyType: l.propertyType,
    location: {
      city: l.location?.city || 'Lebanon',
      area: l.location?.area,
      country: l.location?.country || 'Lebanon',
    },
    pricing: l.pricing,
    rating: l.rating?.average && l.rating.average > 0 ? l.rating.average : undefined,
    reviewCount: l.rating?.count && l.rating.count > 0 ? l.rating.count : undefined,
    likeCount: l.likeCount && l.likeCount > 0 ? l.likeCount : undefined,
    bedrooms: l.capacity?.bedrooms || 1,
    beds: l.capacity?.beds || 1,
    maxGuests: l.capacity?.guests || 2,
    isFeatured: l.isFeatured,
    hostId: l.hostId || '',
    host: l.host
      ? { displayName: l.host.displayName || '', photoURL: l.host.photoURL }
      : undefined,
  }));

  // Related links
  const relatedCities = CITIES.filter((c) => c.slug !== city?.slug).slice(0, 5);
  const relatedTypes = TYPES.filter((t) => t.slug !== type?.slug).slice(0, 5);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary-50 to-primary-100 py-16">
        <Container>
          <Breadcrumbs city={city} type={type} />
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-4">{meta.h1}</h1>
          <p className="text-xl text-gray-700 max-w-3xl">{meta.intro}</p>
          {city && (
            <div className="mt-6 flex flex-wrap gap-2">
              {city.knownFor.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-white/70 rounded-full text-sm text-gray-700">
                  {tag}
                </span>
              ))}
              <span className="px-3 py-1 bg-white/70 rounded-full text-sm text-gray-700">
                Best time: {city.bestTime}
              </span>
            </div>
          )}
        </Container>
      </section>

      {/* Listings */}
      <section className="py-12">
        <Container>
          <div className="flex items-end justify-between mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">
              {isLoading ? 'Loading…' : `${cards.length} place${cards.length === 1 ? '' : 's'} to stay`}
            </h2>
            {!isLoading && cards.length > 0 && (
              <Link
                to={`/search${city ? `?location=${city.slug}` : ''}${type ? `${city ? '&' : '?'}type=${type.slug}` : ''}`}
                className="text-primary-600 hover:underline text-sm"
              >
                Open in search →
              </Link>
            )}
          </div>

          {isLoading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 rounded-xl aspect-[4/3] mb-3" />
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : cards.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-gray-600 mb-4">
                No {type?.plural.toLowerCase() || 'places'} live in {city?.name || 'this area'} yet.
              </p>
              <Link to="/search" className="text-primary-600 hover:underline">
                Browse all listings →
              </Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {cards.map((listing) => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          )}
        </Container>
      </section>

      {/* Editorial body */}
      <section className="py-12 bg-gray-50">
        <Container>
          <div className="max-w-3xl mx-auto prose prose-lg">
            <h2 className="text-3xl font-bold text-gray-900">
              About {city ? city.name : type?.plural.toLowerCase()}
            </h2>
            {meta.body.map((p, i) => (
              <p key={i} className="text-gray-700 leading-relaxed mt-4">
                {p}
              </p>
            ))}
          </div>
        </Container>
      </section>

      {/* Internal links */}
      <section className="py-12">
        <Container>
          <div className="grid md:grid-cols-2 gap-12">
            {city && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  By type in {city.name}
                </h3>
                <ul className="space-y-2">
                  {TYPES.slice(0, 5).map((t) => (
                    <li key={t.slug}>
                      <Link
                        to={`/lebanon/${city.slug}/${t.slug}`}
                        className="text-primary-600 hover:underline"
                      >
                        {t.plural} in {city.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {city ? 'Other Lebanese destinations' : type ? 'Other property types' : 'Explore'}
              </h3>
              <ul className="space-y-2">
                {city
                  ? relatedCities.map((c) => (
                      <li key={c.slug}>
                        <Link to={`/lebanon/${c.slug}`} className="text-primary-600 hover:underline">
                          Stay in {c.name}
                        </Link>
                      </li>
                    ))
                  : relatedTypes.map((t) => (
                      <li key={t.slug}>
                        <Link to={`/property/${t.slug}`} className="text-primary-600 hover:underline">
                          {t.plural} in Lebanon
                        </Link>
                      </li>
                    ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>
    </PublicLayout>
  );
};

const Breadcrumbs: React.FC<{ city?: CityContent; type?: TypeContent }> = ({ city, type }) => {
  return (
    <nav className="text-sm text-gray-600">
      <Link to="/" className="hover:text-gray-900">Home</Link>
      <span className="mx-2">/</span>
      {city ? (
        <>
          <Link to="/search" className="hover:text-gray-900">Lebanon</Link>
          <span className="mx-2">/</span>
          {type ? (
            <>
              <Link to={`/lebanon/${city.slug}`} className="hover:text-gray-900">
                {city.name}
              </Link>
              <span className="mx-2">/</span>
              <span className="text-gray-900">{type.plural}</span>
            </>
          ) : (
            <span className="text-gray-900">{city.name}</span>
          )}
        </>
      ) : type ? (
        <>
          <Link to="/search" className="hover:text-gray-900">Lebanon</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-900">{type.plural}</span>
        </>
      ) : null}
    </nav>
  );
};

export default LandingPage;
